import React, {useEffect, useRef, useState} from 'react';
import {Alert, Box, Button, CircularProgress, Typography} from '@mui/material';
import {Link as RouterLink, useNavigate} from 'react-router-dom';
import {useQueryClient} from '@tanstack/react-query';
import {useAuth} from '../providers/AuthProvider';
import {finishSso} from '../services/authService';
import {apiErrorMessage, isNetworkError} from '../utilities/apiError';
import {ssoCodeFromSearch} from '../utilities/sso';

// Where Google and Apple end up (through the API): the address carries a one-time code, which is traded for a session here.
// A code works once, so a page that is rendered twice (React's development mode does that) must not use it twice: the trade is started
// once per page, whatever the number of renders.
export default function SsoCallbackPage() {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const {refreshAuth} = useAuth();
    const [error, setError] = useState('');
    const started = useRef(false);

    useEffect(() => {
        if (started.current) return;
        started.current = true;

        const code = ssoCodeFromSearch(window.location.search);
        // The code is no use to anyone after this, but it need not stay in the address bar or the history.
        window.history.replaceState(null, '', window.location.pathname);

        if (!code) {
            setError('This page is where signing in with Google or Apple ends up. Please start from the login page.');
            return;
        }

        finishSso(code).then(async () => {
            // Whatever was cached belongs to whoever was signed in before.
            queryClient.clear();
            await refreshAuth();
            navigate('/', {replace: true});
        }, (failure) => {
            setError(isNetworkError(failure) ? 'Network error. Please try again later.' : apiErrorMessage(failure, 'That sign-in did not complete. Please try again.'));
        });
    }, []);

    return (
        <Box sx={{maxWidth: 400, mx: 'auto', mt: 6, p: 3, textAlign: 'center'}}>
            {error ? (
                <>
                    <Alert severity="error" sx={{mb: 2, textAlign: 'left'}}>{error}</Alert>
                    <Button component={RouterLink} to="/login" variant="contained">Back to login</Button>
                </>
            ) : (
                <>
                    <CircularProgress aria-label="Signing in"/>
                    <Typography sx={{mt: 2}}>Signing you in…</Typography>
                </>
            )}
        </Box>
    );
}
